import { FC, useId } from 'react';

import { Button, Icon, Spinner, Tooltip } from '@components/index';
import { UploadStatus } from '@features/lead-management/constants/common';
import { SelectedFileItem } from '@features/lead-management/types/leadFileUpload';
import useIsTruncated from '@hooks/useIsTruncated';

// Helper to compute a stable key for React list rendering
const getFileKey = (file: SelectedFileItem): string =>
  file.objectKey || file.id || `${file.name}-${file.sizeLabel || ''}`;

const FileNameLabel = ({ name }: { name: string }) => {
  const tooltipId = useId();
  const { ref, isTruncated } = useIsTruncated<HTMLDivElement>(name);
  return (
    <>
      <div
        ref={ref}
        data-tooltip-id={tooltipId}
        className="w-full truncate text-sm font-medium text-content"
      >
        {name}
      </div>
      {isTruncated && (
        <Tooltip id={tooltipId} className="rounded-lg !bg-cofeeNeutral10">
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {name}
          </div>
        </Tooltip>
      )}
    </>
  );
};

type LeadFileCardProps = {
  files: SelectedFileItem[];
  onRemove?: (file: SelectedFileItem) => void;
  showClose?: boolean;
  handleDownload?: (file: SelectedFileItem) => void;
  handleDeleteNote?: (file: SelectedFileItem) => void;
  showViewButton?: boolean;
  showDownloadButton?: boolean;
  handleViewFile?: (file: SelectedFileItem) => void;
  showDeleteButton?: boolean;
};

const StatusPill = ({ status }: { status: UploadStatus }) => {
  if (status === UploadStatus.FAILED) {
    return (
      <div className="text-xs font-medium text-[#CF392E]">
        Failed to upload...
      </div>
    );
  }
  return (
    <div className="text-xs font-medium text-verifiedGreen">Uploading...</div>
  );
};

const CardIcon = ({ status }: { status: UploadStatus }) => {
  if (status === UploadStatus.FAILED) {
    return (
      <div className="flex size-9 items-center justify-center rounded-2xl">
        <Icon name="alert-circle" className="!size-8" stroke="#CF392E" />
      </div>
    );
  }
  if (status === UploadStatus.COMPLETED) {
    return (
      <div className="flex size-9 items-center justify-center rounded-2xl bg-[#F3FFF9]">
        <Icon
          name="file-plain"
          className="!size-5 text-grey"
          style={{ color: '#00A657' }}
        />
      </div>
    );
  }
  return (
    <div className="flex size-9 items-center justify-center rounded-2xl">
      <Spinner className="!size-8" />
    </div>
  );
};

const LeadFileCard: FC<LeadFileCardProps> = ({
  files,
  onRemove,
  showClose = true,
  handleDownload,
  handleDeleteNote,
  showViewButton = false,
  showDownloadButton = false,
  handleViewFile,
  showDeleteButton = false
}) => (
  <div className="flex flex-col gap-3">
    {files.map((file) => (
      <div
        key={getFileKey(file)}
        className="flex items-center justify-between gap-4 rounded-lg border border-[#E4E4E4] bg-white p-3"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <CardIcon status={file.status} />

          <div className="flex min-w-0 flex-1 flex-col items-start justify-between gap-1">
            <FileNameLabel name={file.name} />
            {file.status === UploadStatus.COMPLETED && file.sizeLabel ? (
              <div className="flex items-center gap-1 text-xs font-medium text-grey">
                <div>{file.sizeLabel}</div>
              </div>
            ) : (
              <StatusPill status={file.status} />
            )}
          </div>
        </div>
        <div className="flex items-center">
          {showClose && (
            <Button variant="transparent" handleClick={() => onRemove?.(file)}>
              <Icon name="close" className="!size-5 text-grey" />
            </Button>
          )}
          {showViewButton && (
            <Button
              variant="transparent"
              handleClick={() => handleViewFile?.(file)}
            >
              <Icon name="eye-grey" className="!size-5 text-grey" />
            </Button>
          )}
          {showDownloadButton && (
            <Button
              variant="transparent"
              handleClick={() => handleDownload?.(file)}
            >
              <Icon name="download-grey" className="!size-5 text-grey" />
            </Button>
          )}
          {showDeleteButton && (
            <Button
              variant="transparent"
              handleClick={() => handleDeleteNote?.(file)}
            >
              <Icon name="trash" className="!size-5 text-grey" />
            </Button>
          )}
        </div>
      </div>
    ))}
  </div>
);

export default LeadFileCard;
